export interface MSItem {
    type: string,
    number: string,
}

export function processReportJson(
    data: any,
    tClusters?: (key: string) => string,
    tPrintoutTypes?: (key: string) => string
): any[] {

    let json: any[] = [];

    const translatePrintoutType = (type: string): string => {
        if (!tPrintoutTypes) return type;
        const key = 'printout-types.' + type;
        const translated = tPrintoutTypes(key);
        return translated === key ? type : translated;
    };

    const translateClusterType = (clusterType: string): string => {
        if (!tClusters) return clusterType;
        const key = 'cluster-types.' + clusterType;
        const translated = tClusters(key);
        return translated === key ? clusterType : translated;
    };

    // Helper to build the column name: "TYPE" for STANDARD, "TYPE TranslatedLabel" for others
    const colName = (type: string, clusterType: string): string => {
        const typeLabel = translatePrintoutType(type);
        if (clusterType === 'STANDARD') return typeLabel;
        const label = translateClusterType(clusterType);
        return `${typeLabel} ${label}`;
    };

    // Group by msName only
    let map = new Map<string, any[]>();
    data.forEach((it: any) => {
        if (!map.has(it.msName)) {
            map.set(it.msName, []);
        }
        map.get(it.msName)!.push(it);
    });

    // Collect all dynamic column names (preserving insertion order)
    let colsOrdered: string[] = [];
    let colsSet = new Set<string>();
    const colMeta = new Map<string, { type: string; clusterType: string; label: string }>();
    const typeOrder = new Map<string, number>();
    let typeIndex = 0;

    data.forEach((it: any) => {
        if (!typeOrder.has(it.type)) {
            typeOrder.set(it.type, typeIndex++);
        }
        const col = colName(it.type, it.clusterType);
        if (!colsSet.has(col)) {
            colsSet.add(col);
            colsOrdered.push(col);
            colMeta.set(col, { type: it.type, clusterType: it.clusterType, label: col });
        }
    });

    // Sort columns: by type appearance, then STANDARD first, then by label
    colsOrdered.sort((a, b) => {
        const aMeta = colMeta.get(a)!;
        const bMeta = colMeta.get(b)!;
        const typeCmp = (typeOrder.get(aMeta.type) || 0) - (typeOrder.get(bMeta.type) || 0);
        if (typeCmp !== 0) return typeCmp;
        if (aMeta.clusterType === 'STANDARD' && bMeta.clusterType !== 'STANDARD') return -1;
        if (aMeta.clusterType !== 'STANDARD' && bMeta.clusterType === 'STANDARD') return 1;
        return aMeta.label.localeCompare(bMeta.label);
    });

    // Totals accumulator per column
    let totalsMap = new Map<string, number>();

    map.forEach((items) => {
        let obj: any = {};
        obj["MS Name"]         = items[0].msName;
        obj["SAP Code"]        = items[0].msSapId;
        obj["Vendor SAP Code"] = items[0].msVendorId;

        // Initialize all dynamic columns to 0
        colsOrdered.forEach(col => { obj[col] = 0; });

        items.forEach((it: any) => {
            const col = colName(it.type, it.clusterType);
            let amount: number;
            if (it.type === 'FUEL') {
                it.totalFuel   = checkAmount(it.totalFuel);
                it.totalRefuel = checkAmount(it.totalRefuel);
                amount = it.totalFuel as number || 0;

                const refuelCol = colName('REFUEL', it.clusterType);
                if (!colsSet.has(refuelCol)) {
                    colsSet.add(refuelCol);
                    colsOrdered.push(refuelCol);
                    obj[refuelCol] = 0;
                }
                obj[refuelCol] = (obj[refuelCol] || 0) + (it.totalRefuel as number || 0);
                totalsMap.set(refuelCol, (totalsMap.get(refuelCol) || 0) + (it.totalRefuel as number || 0));
            } else {
                it.totalAmount = checkAmount(it.totalAmount);
                amount = it.totalAmount as number || 0;
            }
            obj[col] = (obj[col] || 0) + amount;
            totalsMap.set(col, (totalsMap.get(col) || 0) + amount);
        });

        json.push(obj);
    });

    // Totals row
    let finalObj: any = {};
    finalObj["MS Name"]         = '';
    finalObj["SAP Code"]        = '';
    finalObj["Vendor SAP Code"] = '';
    colsOrdered.forEach(col => {
        finalObj[col] = totalsMap.get(col) || 0;
    });

    json.push(finalObj);
    console.log(json);

    return json;
}

function checkAmount(amount: number): number {
    if (Math.abs(amount) > 0) {
        return amount / 100;
    }
    return amount;
}

export function downloadFilePdf(pdf: string, fileName: any) {
    const linkSource = `data:application/pdf;base64,${pdf}`;
    const downloadLink = document.createElement("a");
    downloadLink.href = linkSource;
    downloadLink.download = fileName;
    downloadLink.click();
}
